import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  FilterState,
  PaginationState,
  OrderByOptions,
} from "../productSlice";

/**
 * Read URL parameters and update Redux state
 * @param {Function} setFilters - Function to update filters state
 * @param {Function} setPagination - Function to update pagination state
 * @returns {boolean} urlSyncComplete - Whether the initial URL sync is complete
 */
export const useProductsUrlToState = (
  setFilters: (filters: Partial<FilterState>) => void,
  setPagination: (pagination: Partial<PaginationState>) => void
): boolean => {
  const [searchParams] = useSearchParams();
  const [syncComplete, setSyncComplete] = useState(false);

  useEffect(() => {
    const filtersToUpdate: Partial<FilterState> = {};
    const paginationToUpdate: Partial<PaginationState> = {};

    // Read parameters from URL
    if (searchParams.has("category")) {
      // Support multiple category parameters
      filtersToUpdate.category = searchParams.getAll("category").map(Number); // Convert to numbers
    }
    if (searchParams.has("productTag")) {
      filtersToUpdate.tag = searchParams.getAll("productTag").map(Number); // Convert to numbers
    }
    if (searchParams.has("brand")) {
      filtersToUpdate.brand = searchParams.getAll("brand"); // Keep as strings for slugs
    }
    if (searchParams.has("orderby")) {
      const orderbyValue = searchParams.get("orderby");
      filtersToUpdate.orderby = orderbyValue
        ? (orderbyValue as OrderByOptions)
        : undefined;
    }
    if (searchParams.has("s")) {
      filtersToUpdate.search = searchParams.get("s") || "";
    }
    if (searchParams.has("minPrice")) {
      const minPrice = searchParams.get("minPrice");
      filtersToUpdate.minPrice = minPrice ? parseFloat(minPrice) : null;
    }
    if (searchParams.has("maxPrice")) {
      const maxPrice = searchParams.get("maxPrice");
      filtersToUpdate.maxPrice = maxPrice ? parseFloat(maxPrice) : null;
    }
    if (searchParams.has("onSale")) {
      filtersToUpdate.onSale = searchParams.get("onSale") === "true";
    }
    if (searchParams.has("page")) {
      const page = searchParams.get("page");
      paginationToUpdate.currentPage = page ? parseInt(page, 10) : 1;
    }
    if (searchParams.has("perPage")) {
      const perPage = searchParams.get("perPage");
      paginationToUpdate.perPage = perPage ? parseInt(perPage, 10) : 10;
    }

    // Update Redux state if needed
    if (Object.keys(filtersToUpdate).length > 0) {
      setFilters(filtersToUpdate);
    }
    if (Object.keys(paginationToUpdate).length > 0) {
      setPagination(paginationToUpdate);
    }

    // Mark sync as complete
    setSyncComplete(true);
  }, [searchParams, setFilters, setPagination]);

  return syncComplete;
};

/**
 * Update URL based on Redux state changes
 */
export const useProductsStateToUrl = (
  filters: FilterState,
  pagination: PaginationState
): void => {
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.category && filters.category.length) {
      // Each category ID gets its own parameter in the URL
      filters.category.forEach((cat) =>
        params.append("category", cat.toString())
      );
    }
    if (filters.tag && filters.tag.length) {
      filters.tag.forEach((tag) => params.append("productTag", tag.toString()));
    }
    if (filters.brand && filters.brand.length) {
      // Use brand slugs directly
      filters.brand.forEach((brandSlug) => params.append("brand", brandSlug));
    }
    if (filters.orderby) {
      params.set("orderby", filters.orderby);
    }
    if (filters.search) {
      params.set("s", filters.search);
    }
    if (filters.minPrice !== null) {
      params.set("minPrice", filters.minPrice.toString());
    }
    if (filters.maxPrice !== null) {
      params.set("maxPrice", filters.maxPrice.toString());
    }
    if (filters.onSale) {
      params.set("onSale", "true");
    }
    if (pagination.currentPage > 1) {
      params.set("page", pagination.currentPage.toString());
    }
    if (pagination.perPage !== 10) {
      params.set("perPage", pagination.perPage.toString());
    }

    // Update URL without causing re-render cycles
    setSearchParams(params, { replace: true });
  }, [filters, pagination, setSearchParams]);
};

/**
 * Combined hook for bidirectional URL-state synchronization
 * @param {Object} filters - Current filters state
 * @param {Object} pagination - Current pagination state
 * @param {Function} setFilters - Function to update filters state
 * @param {Function} setPagination - Function to update pagination state
 * @returns {boolean} urlSyncComplete - Whether the initial URL sync is complete
 */
export const useProductsUrlSync = (
  filters: FilterState,
  pagination: PaginationState,
  setFilters: (filters: Partial<FilterState>) => void,
  setPagination: (pagination: Partial<PaginationState>) => void
): boolean => {
  const urlSyncComplete = useProductsUrlToState(setFilters, setPagination);
  useProductsStateToUrl(filters, pagination);

  return urlSyncComplete;
};
